import { Sprite, type Texture } from 'pixi.js';
import { signInFromMenu } from '../../ui/account';
import { canUseWorldChat } from '../systems/policy';
import { canFullscreen, isInstalled, toggleFullscreen } from '../../app/fullscreen';
import type { App } from '../../app/App';
import { Scene } from '../core/Scene';
import type { ViewportInfo } from '../core/Viewport';
import { FighterView } from '../entities/FighterView';
import { ParticlePool } from '../entities/Effects';
import { acquireArt, nominalScale } from '../entities/art';
import { Ease } from '../animation/Ease';
import { motion } from '../animation/Motion';
import { MOVES, judge } from '../combat/rps';
import { PRESET_MONSTERS } from '../data/presets';
import { button } from '../../ui/components';
import { h } from '../../ui/dom';
import { deskProps, paperScenery } from '../../ui/notebookArt';
import { icon } from '../../ui/icons';
import { skinUrl } from '../../ui/skin';
import { t } from '../../i18n';
import { openDiceBoard } from '../../ui/diceBoard';
import { openFortuneTeller } from './notebook/fortune';
import { openRankings } from '../../ui/rankings';
import { APP_VERSION } from '../../app/version';
import { changelogUnseen, openChangelog } from '../../ui/changelog';
import { openFeedback } from '../../ui/feedback';
import { heroArtId, heroDrawing } from '../systems/hero';

/** SCREEN 01 — Main menu on an animated notebook page. */
/**
 * Scrolls only the page that holds `el` (scrollIntoView would also shift the
 * whole scaled stage, pushing the top bar off the screen).
 */
function scrollWithinPage(el: HTMLElement | null): void {
  let box = el?.parentElement ?? null;
  while (box && !(box.scrollHeight > box.clientHeight && /auto|scroll/.test(getComputedStyle(box).overflowY))) box = box.parentElement;
  if (!el || !box) return;
  const r = box.getBoundingClientRect();
  // The stage is scaled: screen pixels back to the page's own pixels.
  const scale = r.height > 0 ? box.clientHeight / r.height : 1;
  const top = (el.getBoundingClientRect().top - r.top) * scale + box.scrollTop;
  box.scrollTo({ top, behavior: 'smooth' });
}

export class MenuScene extends Scene {
  private hero!: FighterView;
  private monster!: FighterView;
  private particles = new ParticlePool(40);
  private moveIcons: Sprite[] = [];
  private heroAnchor!: HTMLElement;
  private monsterAnchor!: HTMLElement;
  private clashTimer = 2200;
  private clashing = false;
  private driftTimer = 0;

  constructor(app: App) {
    super(app, 'scene-menu page');
  }

  enter(): void {
    const app = this.app;
    const hero = app.store.hero;
    const go = (fn: () => void) => () => fn();

    this.heroAnchor = h('div', { class: 'stage-anchor hero-anchor', 'aria-hidden': 'true' });
    this.monsterAnchor = h('div', { class: 'stage-anchor monster-anchor', 'aria-hidden': 'true' });
    const status = h('span', { class: 'server-status' }, t('checking the battle server…'));

    // Menu entries: hand-drawn icon + label; the highlighted one gets the red ▶ marker.
    const entry = (iconName: string, label: string, onClick: () => void, primary = false): HTMLButtonElement => {
      const b = button(h('span', { class: 'menu-item-label' }, icon(iconName, 'menu-item-icon'), h('span', null, label)), {
        variant: primary ? 'primary' : 'secondary',
        color: primary ? 'yellow' : 'paper',
        sfx: app,
        onClick: go(onClick),
      });
      b.classList.add('menu-item');
      const select = (): void => {
        for (const other of b.parentElement?.querySelectorAll('.menu-item') ?? []) other.classList.toggle('selected', other === b);
      };
      b.addEventListener('mouseenter', select);
      b.addEventListener('focus', select);
      return b;
    };
    // Start Adventure opens the notebook at the hero select; everything else lives behind its bookmarks.
    const items = [
      entry('swords', t('Start Adventure'), () => void app.scenes.go('notebook', { tab: hero ? 'hero' : 'newhero' }, 'cover'), true),
      entry('kids', t('Battle With Friends'), () => void app.scenes.go('multiplayer', undefined, 'page')),
      entry('trophy', t('Rankings'), () => void openRankings(app)),
    ];
    items[0]!.classList.add('selected');
    // Opens a screen and scrolls to one of its sections once it is drawn.
    const goTo = (scene: 'notebook' | 'multiplayer', params: unknown, selector: string): void => {
      void app.scenes.go(scene, params as never, scene === 'notebook' ? 'cover' : 'page').then(() =>
        window.setTimeout(() => scrollWithinPage(document.querySelector<HTMLElement>(selector)), 450),
      );
    };
    // The last row shares one line: Options plus shortcuts to the shop and the world chat,
    // so the newer features are one tap away without making the menu taller.
    const quick = h(
      'div',
      { class: 'menu-quick' },
      entry('gear', t('Options'), () => void app.scenes.go('notebook', { tab: 'settings' }, 'cover')),
      // The shop works without a hero (it only can't equip); always open it.
      entry('coin', t('Shop'), () => goTo('notebook', { tab: 'equipment' }, '.premium-shop')),
      entry('dice', t('Dice'), () => void openDiceBoard(app)),
      entry('ticket', t('Fortune'), () => void openFortuneTeller(app)),
      canUseWorldChat(app.store.state) ? entry('plane', t('Chat'), () => goTo('multiplayer', undefined, '.world-chat')) : null,
    );
    // Who is signed in, and one tap to the Account settings.
    const online = app.store.state.online;
    const account = button(online ? t('{name} · account', { name: online.name }) : t('Sign in'), {
      small: true,
      color: online ? 'paper' : 'blue',
      sfx: app,
      onClick: () => (online ? goTo('notebook', { tab: 'settings' }, '.account-section') : void signInFromMenu(app).then(() => this.app.scenes.go('menu', undefined, 'none'))),
    });
    account.classList.add('menu-account');
    // What's new: a dot until the newest notes have been opened on this device.
    const updates = button(`📜 ${t('Update Log')}`, {
      variant: 'ghost',
      small: true,
      sfx: app,
      onClick: () => {
        updates.classList.remove('has-news');
        void openChangelog(app);
      },
    });
    updates.classList.add('menu-updates');
    updates.classList.toggle('has-news', changelogUnseen());
    if (online) {
      app.api.token = online.token;
      app.api.account().then(
        (a) => {
          const label = account.querySelector('.label');
          if (label) label.textContent = a.google !== null ? t('{name} · Google ✓', { name: online.name }) : t('{name} · Guest — link Google', { name: online.name });
        },
        () => {},
      );
    }

    this.dom.append(
      h('div', { class: 'spiral', 'aria-hidden': 'true' }),
      paperScenery('menu-scenery'),
      deskProps(),
      h('p', { class: 'menu-scribble left', 'aria-hidden': 'true' }, t('Small drawings.'), h('br'), t('Big adventures!'), ' ☺'),
      h('p', { class: 'menu-scribble right', 'aria-hidden': 'true' }, t('Monsters today.'), h('br'), t('Legends tomorrow!'), ' ', icon('crown', 'tiny')),
      h(
        'header',
        { class: 'logo-wrap' },
        h('h1', { class: 'logo', 'aria-label': 'Paper Legends' }, skinUrl('title/paper-legends') ? h('img', { class: 'art-img logo-art', src: skinUrl('title/paper-legends'), alt: '', draggable: false, 'aria-hidden': 'true' }) : logoSvg()),
        h('div', { class: 'tape-label ribbon' }, t('A notebook RPG where your drawings come alive')),
      ),
      h('div', { class: 'menu-stage hero-side' }, this.heroAnchor),
      h(
        'nav',
        { class: 'menu-buttons', 'aria-label': t('Main menu') },
        ...items,
        quick,
      ),
      h('div', { class: 'menu-stage monster-side' }, this.monsterAnchor),
      h('div', { class: 'menu-sticky sticky-note yellow', 'aria-hidden': 'true' }, icon('trophy'), h('p', null, t('Same notebook.'), h('br'), t('New legends.'))),
      h('footer', { class: 'menu-footer paper-scrap' },
        h('span', { class: 'prototype-note' }, t('Prototype UI ☺')),
        button(t('How to Play'), { variant: 'ghost', small: true, sfx: app, onClick: go(() => void app.scenes.go('help', undefined, 'page')) }),
        button(t('💡 Suggest an idea'), { variant: 'ghost', small: true, sfx: app, onClick: () => void openFeedback(app) }),
        updates,
        account,
        canFullscreen() && !isInstalled() ? button(t('Full screen'), { variant: 'ghost', small: true, sfx: app, onClick: () => toggleFullscreen() }) : null,
        status, h('span', { class: 'version' }, t('v{version} · made with pencils', { version: APP_VERSION }))),
    );

    // Pixi: the two doodles on the page.
    const heroArt = acquireArt(app, hero && heroDrawing(hero), hero ? heroArtId(hero) : 'hero:pencil_knight');
    const preset = PRESET_MONSTERS.find(m => m.id === 'eraser_golem')!;
    const monsterArt = acquireArt(app, null, `monster:${preset.id}`);
    this.d.add(heroArt.release);
    this.d.add(monsterArt.release);
    this.hero = new FighterView(heroArt, 1, app.tweens);
    this.hero.setRestingPose('attack');
    this.monster = new FighterView(monsterArt, -1, app.tweens);
    for (let i = 0; i < 2; i++) {
      const s = new Sprite(app.assets.get('move:rock')[0]);
      s.anchor.set(0.5);
      s.visible = false;
      this.moveIcons.push(s);
    }
    this.root.addChild(this.hero, this.monster, ...this.moveIcons, this.particles);

    app.api
      .health()
      .then((r) => {
        status.textContent = t('battle server: online ({storage})', { storage: r.storage });
        status.classList.add('ok');
      })
      .catch(() => {
        status.textContent = t('battle server: offline — single-player works!');
        status.classList.add('off');
      });
  }

  override shown(): void {
    void this.hero.reveal();
    void this.monster.reveal();
  }

  override layout(_vp: ViewportInfo): void {
    if (!this.heroAnchor.isConnected) return;
    const a = this.anchor(this.heroAnchor);
    const b = this.anchor(this.monsterAnchor);
    this.hero.position.set(a.cx, a.y + a.h);
    this.monster.position.set(b.cx, b.y + b.h);
    this.hero.setHeight(a.h);
    this.monster.setHeight(b.h);
    for (const s of this.moveIcons) s.scale.set(Math.max(0.35, a.h / 420) * nominalScale(s.texture, 128));
  }

  override update(dt: number): void {
    this.hero.update(dt);
    this.monster.update(dt);
    this.particles.update(dt);
    this.driftTimer -= dt;
    if (this.driftTimer <= 0 && !motion.reduced) {
      this.driftTimer = 900;
      const vp = this.app.viewport.info;
      this.particles.burst(this.app.assets.get('fx:paper_scrap'), Math.random() * vp.width, vp.height + 20, 1, { speed: 120, gravity: -10, scale: 0.4, life: 5000 });
    }
    if (!this.clashing) {
      this.clashTimer -= dt;
      if (this.clashTimer <= 0) void this.clash();
    }
  }

  /** A mock rock-paper-scissors exchange to show the game at a glance. */
  private async clash(): Promise<void> {
    this.clashing = true;
    const tw = this.app.tweens;
    const a = MOVES[Math.floor(Math.random() * 3)]!;
    const b = MOVES[Math.floor(Math.random() * 3)]!;
    const [iconA, iconB] = this.moveIcons as [Sprite, Sprite];
    const show = (icon: Sprite, f: FighterView, tex: Texture[]): void => {
      icon.texture = tex[0]!;
      icon.position.set(f.x, f.y - this.anchor(this.heroAnchor).h * 1.12);
      icon.alpha = 0;
      icon.visible = true;
      void tw.to(icon, { alpha: 1, y: icon.y - 14 }, 260, { ease: Ease.outBack });
    };
    show(iconA, this.hero, this.app.assets.get(`move:${a}`));
    show(iconB, this.monster, this.app.assets.get(`move:${b}`));
    await tw.wait(700);
    if (this.isDestroyed) return;
    const w = judge(a, b);
    if (w !== -1) {
      const [att, def] = w === 0 ? [this.hero, this.monster] : [this.monster, this.hero];
      await att.attack(Math.abs(this.monster.x - this.hero.x) * 0.25);
      if (this.isDestroyed) return;
      this.particles.burst(this.app.assets.get('fx:eraser_crumb').concat(this.app.assets.get('fx:paper_scrap')), def.x, def.y - 80, 10, { scale: 0.5 });
      this.app.sfx.play('hit');
      void def.hit(false);
    }
    await tw.wait(600);
    if (this.isDestroyed) return;
    for (const s of this.moveIcons) void tw.to(s, { alpha: 0 }, 200).then(() => !s.destroyed && (s.visible = false));
    this.clashTimer = 3200 + Math.random() * 2000;
    this.clashing = false;
  }
}

/** Crayon-hatch fill pattern for one logo colour. */
function hatch(id: string, base: string, line: string, angle: number): string {
  return `<pattern id="${id}" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(${angle})"><rect width="7" height="7" fill="${base}"/><line x1="0" y1="0" x2="0" y2="7" stroke="${line}" stroke-width="2.6" opacity=".75"/></pattern>`;
}

/**
 * "Paper Legends" lettered like the reference: graphite "Paper", every letter
 * of "Legends" in a different crayon, a crown and sparkles. Placeholder until
 * the skin title is available.
 */
function logoSvg(): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 900 300');
  svg.setAttribute('class', 'logo-svg');
  const legends: [string, string][] = [
    ['L', 'lg-orange'],
    ['e', 'lg-yellow'],
    ['g', 'lg-red'],
    ['e', 'lg-orange'],
    ['n', 'lg-blue'],
    ['d', 'lg-blue2'],
    ['s', 'lg-blue'],
  ];
  svg.innerHTML = `
    <defs>
      ${hatch('lg-graphite', '#5a5a5a', '#2b2b2b', -35)}
      ${hatch('lg-orange', '#f3a712', '#e4572e', 30)}
      ${hatch('lg-yellow', '#f6d55c', '#f3a712', -25)}
      ${hatch('lg-red', '#e4572e', '#b33a3a', 35)}
      ${hatch('lg-blue', '#2e86de', '#1f5fa8', -30)}
      ${hatch('lg-blue2', '#5aa0e6', '#2e86de', 25)}
    </defs>
    <path class="logo-crown" d="M420 38 L426 8 L440 26 L450 4 L460 26 L474 8 L480 38 Z" />
    <text class="logo-line l1" x="450" y="128" text-anchor="middle">Paper</text>
    <text class="logo-line l2" x="450" y="250" text-anchor="middle">${legends.map(([c, f]) => `<tspan fill="url(#${f})">${c}</tspan>`).join('')}</text>
    <path class="logo-spark" d="M140 70 l6 14 l14 6 l-14 6 l-6 14 l-6 -14 l-14 -6 l14 -6 Z M770 40 l5 11 l11 5 l-11 5 l-5 11 l-5 -11 l-11 -5 l11 -5 Z M790 190 l4 9 l9 4 l-9 4 l-4 9 l-4 -9 l-9 -4 l9 -4 Z" />
    <path class="logo-underline" d="M190 272 C 330 262, 560 282, 720 266" />`;
  return svg;
}
